'use client'

import { useEffect, useRef } from 'react'
import { makeNoise, ridgeHeight, SunDisc } from './Ridge'

/*
 * ─── Summit ───
 * The footer's range at night: five dark ridgelines with sharp peaks, snow on the tallest,
 * a warm glow low behind them and mist drifting between. As the footer scrolls into view
 * the ridges rise from below, the nearest fastest, so the range seems to come up to meet
 * you. Scroll progress is written to --rise on the root; the CSS does the moving.
 */

const W = 1600
const H = 600

const LAYERS = [
  // far → near. Far ridges catch the glow; near ones fall into shadow. `snow` is the
  // snowline as a fraction of the canvas height (peaks above it get a cap), `cap` how
  // bright the snow reads at that distance, `rise` how far the layer travels as it rises.
  { seed: 101, base: 0.64, amp: 0.46, scale: 0.0016, sharp: 3.2, oct: 4, fill: '#34302c', snow: 0.42, cap: 0.6, rise: 0.12 },
  { seed: 211, base: 0.72, amp: 0.4, scale: 0.0022, sharp: 2.8, oct: 4, fill: '#2a2724', snow: 0.5, cap: 0.45, rise: 0.22 },
  { seed: 307, base: 0.82, amp: 0.32, scale: 0.003, sharp: 2.4, oct: 3, fill: '#211f1d', snow: 0.62, cap: 0.3, rise: 0.34 },
  { seed: 401, base: 0.9, amp: 0.24, scale: 0.0042, sharp: 1.9, oct: 3, fill: '#1a1817', snow: 0, cap: 0, rise: 0.48 },
  { seed: 503, base: 1.0, amp: 0.16, scale: 0.0056, sharp: 1.5, oct: 2, fill: '#131211', snow: 0, cap: 0, rise: 0.62 },
]

function build(L) {
  const noise = makeNoise(L.seed)
  const jag = makeNoise(L.seed + 7)
  const top = []
  const caps = []
  let cap = null
  for (let x = 0; x <= W; x += 5) {
    const h = ridgeHeight(noise, x * L.scale, L.oct, L.sharp)
    const y = H * L.base - h * H * L.amp
    top.push(`${x},${y.toFixed(1)}`)
    // snow: from the ridge down towards the snowline, with a ragged lower edge
    const line = H * L.snow
    if (L.snow && y < line) {
      const depth = (line - y) * (0.55 + jag(x * 0.03) * 0.35)
      if (!cap) cap = []
      cap.push([x, y, y + depth])
    } else if (cap) {
      caps.push(cap)
      cap = null
    }
  }
  if (cap) caps.push(cap)
  const snow = caps
    .filter((c) => c.length > 1)
    .map((c) => `M${c.map(([x, y]) => `${x},${y.toFixed(1)}`).join(' L')} L${c
      .slice()
      .reverse()
      .map(([x, , b]) => `${x},${b.toFixed(1)}`)
      .join(' L')} Z`)
    .join(' ')
  // the ridge runs well below the canvas so a layer that is still rising never shows its foot
  return { ridge: `M0,${H * 1.6} L${top.join(' L')} L${W},${H * 1.6} Z`, snow }
}

const SHAPES = LAYERS.map(build)

export function Summit({ sun }) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.style.setProperty('--rise', 1)
      return
    }
    let raf = 0
    const update = () => {
      raf = 0
      const r = el.getBoundingClientRect()
      const vh = window.innerHeight
      // 0 when the range's top reaches the bottom of the screen, 1 when the page ends
      const t = (vh - r.top) / Math.max(r.height, 1)
      el.style.setProperty('--rise', Math.min(Math.max(t, 0), 1).toFixed(3))
    }
    const on = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', on, { passive: true })
    window.addEventListener('resize', on)
    return () => {
      window.removeEventListener('scroll', on)
      window.removeEventListener('resize', on)
      cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <div ref={ref} className="sm" aria-hidden="true">
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="sm-snow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#f1ece4" />
            <stop offset="1" stopColor="#c9c1b5" stopOpacity="0.4" />
          </linearGradient>
          <radialGradient id="sm-mistg" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#e9e4dc" stopOpacity="0.09" />
            <stop offset="1" stopColor="#e9e4dc" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
      {!sun && <div className="sm-glow" />}
      {sun && (
        <svg className="sm-layer sm-sun" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMax slice">
          <SunDisc {...sun} idPrefix="sm-sun" />
        </svg>
      )}
      {LAYERS.map((L, i) => (
        <svg
          key={L.seed}
          className="sm-layer"
          style={{ '--k': L.rise }}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMax slice"
        >
          <path d={SHAPES[i].ridge} fill={L.fill} />
          {SHAPES[i].snow && <path d={SHAPES[i].snow} fill="url(#sm-snow)" opacity={L.cap} />}
          {i < 3 && <ellipse className={`sm-mist sm-mist--${i}`} cx={W * (0.3 + i * 0.25)} cy={H * (0.62 + i * 0.1)} rx={W * 0.4} ry={H * 0.07} fill="url(#sm-mistg)" />}
        </svg>
      ))}
    </div>
  )
}
